import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Paper, Alert, IconButton, Grid, CircularProgress
} from '@mui/material';
import { Close, CloudUpload, Delete, Image as ImageIcon } from '@mui/icons-material';
import { uploadWarehousePhotos } from '../../services/api';

const QuickUploadModal = ({ open, onClose, orderId, orderNumber, onUploadSuccess }) => {
  const { t } = useTranslation(['orders', 'common']);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const fileInputRef = useRef(null);
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleFileSelect = useCallback((files) => {
    const newFiles = Array.from(files).filter(file => {
      if (!acceptedFileTypes.includes(file.type)) {
        setMessage(t('orders:modals.quickUpload.invalidFileType', { fileName: file.name }));
        setSeverity('error');
        return false;
      }
      if (file.size > maxFileSize) {
        setMessage(t('orders:modals.quickUpload.fileTooLarge', { fileName: file.name }));
        setSeverity('error');
        return false;
      }
      return true;
    });
    setSelectedFiles(prev => [...prev, ...newFiles]);
    setMessage('');
  }, [maxFileSize, acceptedFileTypes]);

  const handleFileRemove = useCallback((indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  }, [handleFileSelect]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('images', file);
      });

      const response = await uploadWarehousePhotos(orderId, formData);
      
      if (response.success) {
        setMessage(t('orders:modals.quickUpload.uploadSuccess', { count: response.data.uploaded_photos.length }));
        setSeverity('success');
        
        // Call success callback to refresh data
        if (onUploadSuccess) {
          onUploadSuccess(response.data);
        }
        
        // Close modal after a short delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setMessage(response.message || t('orders:modals.quickUpload.uploadFailed'));
        setSeverity('error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage(t('orders:modals.quickUpload.uploadFailedRetry'));
      setSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setMessage('');
    setSeverity('info');
    setLoading(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('orders:modals.quickUpload.title', { orderNumber })}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {message && (
          <Alert severity={severity} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        
        <Paper
          elevation={dragActive ? 8 : 2}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            border: dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
            backgroundColor: dragActive ? '#E8F5E8' : 'transparent',
            transition: 'all 0.3s ease',
            mb: 3,
            '&:hover': {
              backgroundColor: '#E8F5E8',
              borderColor: '#4CAF50'
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes.join(',')}
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('orders:modals.quickUpload.dragDropOrClick')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('orders:modals.quickUpload.fileTypes', { maxSize: Math.round(maxFileSize / (1024 * 1024)) })}
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
            {t('orders:modals.quickUpload.autoUpdateWarning')}
          </Typography>
        </Paper>

        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{t('orders:modals.quickUpload.selectedFiles')}</Typography>
            <Grid container spacing={2}>
              {selectedFiles.map((file, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper elevation={1} sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon fontSize="small" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" noWrap>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatFileSize(file.size)}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleFileRemove(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          {t('orders:modals.quickUpload.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={selectedFiles.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
        >
          {loading ? t('orders:modals.quickUpload.uploading') : t('orders:modals.quickUpload.uploadFiles', { count: selectedFiles.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickUploadModal;

